import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { supabase } from "@/integrations/supabase/client";
import { Logo, PAGE_CONTAINER } from "../../components/site";

export const Route = createFileRoute("/_authenticated/dashboard")({
  head: () => ({
    meta: [
      { title: "Dashboard | offerto" },
      { name: "description", content: "Bezoekers, aanvragen en afgebroken aanvragen voor beheerders." },
      { property: "og:title", content: "Dashboard | offerto" },
      { property: "og:description", content: "Bezoekers, aanvragen en afgebroken aanvragen voor beheerders." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Dashboard,
});

const STAPNAMEN = ["Locatie", "Type opdracht", "Soort aanvraag", "Uitvoerdatum", "Intentie", "Omschrijving & foto's", "Naam", "Contactgegevens", "Verificatiecode"];
const PERIODES = [
  { label: "Vandaag", dagen: 1 },
  { label: "7 dagen", dagen: 7 },
  { label: "30 dagen", dagen: 30 },
  { label: "90 dagen", dagen: 90 },
];

async function alles<T>(maak: (van: number, tot: number) => PromiseLike<{ data: T[] | null; error: unknown }>) {
  const rijen: T[] = [];
  for (let van = 0; ; van += 1000) {
    const { data, error } = await maak(van, van + 999);
    if (error) throw error;
    rijen.push(...(data ?? []));
    if (!data || data.length < 1000) return rijen;
  }
}

function Dashboard() {
  const [dagen, setDagen] = useState(30);
  const vanaf = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - (dagen - 1));
    return d.toISOString();
  })();

  const { data: isAdmin, isPending: rolLaden } = useQuery({
    queryKey: ["is-admin"],
    queryFn: async () => {
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) return false;
      const { data } = await supabase.rpc("has_role", { _user_id: u.user.id, _role: "admin" });
      return data === true;
    },
  });

  const { data, isPending, isError } = useQuery({
    queryKey: ["dashboard", dagen],
    enabled: isAdmin === true,
    queryFn: async () => {
      const [bezoeken, leads, pogingen] = await Promise.all([
        alles<{ sessie_id: string }>((a, b) => supabase.from("bezoeken").select("sessie_id").gte("created_at", vanaf).range(a, b)),
        alles<{ id: string; ai_categorie: string | null; type_opdracht: string | null; bron: string; product: string | null }>((a, b) =>
          supabase.from("leads").select("id, ai_categorie, type_opdracht, bron, product").gte("created_at", vanaf).range(a, b)),
        alles<{
          id: string; updated_at: string; laatste_stap: number; postcode: string | null; plaats: string | null;
          type_opdracht: string | null; voornaam: string | null; achternaam: string | null; telefoon: string | null; email: string | null;
          omschrijving: string | null; bron: string; product: string | null;
        }>((a, b) =>
          supabase.from("aanvraag_pogingen")
            .select("id, updated_at, laatste_stap, postcode, plaats, type_opdracht, voornaam, achternaam, telefoon, email, omschrijving, bron, product")
            .eq("voltooid", false).gte("created_at", vanaf).order("updated_at", { ascending: false }).range(a, b)),
      ]);
      const perCategorie = new Map<string, number>();
      for (const l of leads) {
        const c = l.type_opdracht || l.ai_categorie || "Onbekend";
        perCategorie.set(c, (perCategorie.get(c) ?? 0) + 1);
      }
      const perStap = STAPNAMEN.map((_, i) => pogingen.filter((p) => p.laatste_stap === i).length);
      return {
        paginaweergaven: bezoeken.length,
        bezoekers: new Set(bezoeken.map((b) => b.sessie_id)).size,
        succesvol: leads.length,
        aanvragen: leads,
        categorieen: [...perCategorie.entries()].sort((a, b) => b[1] - a[1]),
        afgebroken: pogingen,
        perStap,
      };
    },
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className={`${PAGE_CONTAINER} flex flex-wrap items-center justify-between gap-4 py-4`}>
          <Logo />
          <nav className="flex gap-4 text-sm font-semibold">
            <Link to="/admin" className="text-primary hover:underline">Beheer</Link>
            <Link to="/verzonden-aanvragen" className="text-primary hover:underline">Verzonden aanvragen</Link>
          </nav>
        </div>
      </header>
      <main className={`${PAGE_CONTAINER} py-8`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <div className="flex gap-1 rounded-lg bg-secondary p-1">
            {PERIODES.map((p) => (
              <button key={p.dagen} type="button" onClick={() => setDagen(p.dagen)}
                className={`rounded-md px-3 py-1.5 text-sm font-bold ${dagen === p.dagen ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {rolLaden && <p className="mt-8 text-muted-foreground">Toegang controleren…</p>}
        {!rolLaden && !isAdmin && <p className="mt-8 text-muted-foreground">Geen toegang tot dit dashboard.</p>}
        {isAdmin && isPending && <p className="mt-8 text-muted-foreground">Cijfers laden…</p>}
        {isAdmin && isError && <p className="mt-8 text-destructive">De cijfers konden niet worden geladen. Probeer het opnieuw.</p>}

        {isAdmin && data && (
          <>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Kaart titel="Bezoekers" waarde={data.bezoekers} toelichting={`${data.paginaweergaven} paginaweergaven`} />
              <Kaart titel="Succesvolle aanvragen" waarde={data.succesvol}
                toelichting={data.bezoekers ? `${((data.succesvol / data.bezoekers) * 100).toFixed(1)}% van de bezoekers` : "—"} />
              <Kaart titel="Afgebroken aanvragen" waarde={data.afgebroken.length} toelichting="Begonnen, niet verzonden" />
              <Kaart titel="Afrondingspercentage"
                waarde={data.succesvol + data.afgebroken.length ? `${Math.round((data.succesvol / (data.succesvol + data.afgebroken.length)) * 100)}%` : "—"}
                toelichting="Van gestarte aanvragen" />
            </div>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
              <section className="rounded-2xl border border-border p-5">
                <h2 className="text-lg font-bold">Succesvolle aanvragen per categorie</h2>
                {data.categorieen.length === 0 ? <p className="mt-4 text-sm text-muted-foreground">Nog geen aanvragen in deze periode.</p> : (
                  <ul className="mt-4 space-y-3">
                    {data.categorieen.map(([naam, aantal]) => (
                      <Balk key={naam} label={naam} aantal={aantal} max={data.categorieen[0]?.[1] ?? 1} />
                    ))}
                  </ul>
                )}
              </section>
              <section className="rounded-2xl border border-border p-5">
                <h2 className="text-lg font-bold">Waar aanvragen worden onderbroken</h2>
                <ul className="mt-4 space-y-3">
                  {STAPNAMEN.slice(1).map((naam, i) => (
                    <Balk key={naam} label={`Stap ${i + 2}: ${naam}`} aantal={data.perStap[i + 1] ?? 0} max={Math.max(1, ...data.perStap)} />
                  ))}
                </ul>
              </section>
            </div>

            <section className="mt-8">
              <h2 className="text-lg font-bold">Bron en product van succesvolle aanvragen</h2>
              <div className="mt-3 overflow-x-auto border-y border-border">
                <table className="w-full text-left text-sm">
                  <thead className="bg-secondary"><tr>{["Categorie", "Bron", "Product"].map((k) => <th key={k} scope="col" className="px-3 py-3 font-semibold">{k}</th>)}</tr></thead>
                  <tbody className="divide-y divide-border">{data.aanvragen.map((l) => <tr key={l.id} className="align-top"><td className="px-3 py-3">{l.type_opdracht || l.ai_categorie || "—"}</td><td className="px-3 py-3 break-words">{l.bron}</td><td className="px-3 py-3 break-words">{l.product || "—"}</td></tr>)}</tbody>
                </table>
              </div>
            </section>
            <section className="mt-8">
              <h2 className="text-lg font-bold">Afgebroken aanvragen</h2>
              {data.afgebroken.length === 0 ? <p className="mt-3 text-sm text-muted-foreground">Geen afgebroken aanvragen in deze periode.</p> : (
                <div className="mt-3 overflow-x-auto border-y border-border">
                  <table className="w-full min-w-[800px] text-left text-sm">
                    <thead className="bg-secondary">
                      <tr>
                        {["Laatst actief", "Gestopt bij", "Postcode", "Werkzaamheden", "Bron", "Product", "Naam", "Telefoon", "E-mail"].map((k) => (
                          <th key={k} scope="col" className="px-3 py-3 font-semibold">{k}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {data.afgebroken.map((p) => (
                        <tr key={p.id} className="align-top">
                          <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">
                            {new Date(p.updated_at).toLocaleString("nl-NL", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                          </td>
                          <td className="px-3 py-3 font-semibold">{STAPNAMEN[p.laatste_stap] ?? `Stap ${p.laatste_stap + 1}`}</td>
                          <td className="px-3 py-3">{[p.postcode, p.plaats].filter(Boolean).join(" ") || "—"}</td>
                          <td className="px-3 py-3">{p.type_opdracht || "—"}</td>
                          <td className="px-3 py-3 break-words">{p.bron}</td>
                          <td className="px-3 py-3 break-words">{p.product || "—"}</td>
                          <td className="px-3 py-3">{[p.voornaam, p.achternaam].filter(Boolean).join(" ") || "—"}</td>
                          <td className="px-3 py-3">{p.telefoon ? <a className="text-primary hover:underline" href={`tel:${p.telefoon}`}>{p.telefoon}</a> : "—"}</td>
                          <td className="px-3 py-3 break-all">{p.email ? <a className="text-primary hover:underline" href={`mailto:${p.email}`}>{p.email}</a> : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Kaart({ titel, waarde, toelichting }: { titel: string; waarde: number | string; toelichting: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <p className="text-sm font-semibold text-muted-foreground">{titel}</p>
      <p className="mt-2 text-3xl font-bold">{waarde}</p>
      <p className="mt-1 text-xs text-muted-foreground">{toelichting}</p>
    </div>
  );
}

function Balk({ label, aantal, max }: { label: string; aantal: number; max: number }) {
  return (
    <li>
      <div className="flex justify-between gap-3 text-sm"><span>{label}</span><span className="font-bold">{aantal}</span></div>
      <div className="mt-1 h-2 rounded-full bg-secondary">
        <div className="h-2 rounded-full bg-primary" style={{ width: `${max ? (aantal / max) * 100 : 0}%` }} />
      </div>
    </li>
  );
}
